import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RSVP_META = {
  aceptado:  { icon: '✓', label: 'Aceptada',   cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  tal_vez:   { icon: '?', label: 'Tal vez',    cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  rechazado: { icon: '✕', label: 'Rechazada',  cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  pendiente: { icon: '•', label: 'Pendiente',  cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

/**
 * ReunionesSalaDocenteWidget
 * Muestra las próximas reuniones de Sala Docente donde el profesor está invitado.
 * Cada card lleva a la tarjeta correspondiente en Sala Docente (deep-link).
 */
const ReunionesSalaDocenteWidget = ({ user }) => {
  const [reuniones, setReuniones] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    // Solo mostrar para profesor / admin
    if (user.rol !== 'profesor' && user.rol !== 'admin') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/area-profesores/reuniones?estado=proximas`);
        const list = r.data?.reuniones || [];
        // Solo las que soy invitado (no las que organicé). El endpoint devuelve ambas.
        const invitadas = list.filter(x => !x.soy_organizador);
        if (!cancelled) setReuniones(invitadas);
      } catch (e) {
        console.error('Widget reuniones sala docente:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (loading) return null;
  if (user?.rol !== 'profesor' && user?.rol !== 'admin') return null;
  if (reuniones.length === 0) return null;

  const goToReunion = (reunionId) => {
    navigate(`/app/sala-docente?tab=reuniones&reunionId=${reunionId}`);
  };

  return (
    <Card className="col-span-full lg:col-span-2 shadow-md border-indigo-100" data-testid="widget-reuniones-sala-docente">
      <CardHeader className="pb-2 bg-gradient-to-r from-indigo-50 to-violet-50">
        <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
          <i className="fas fa-users text-indigo-600"></i>
          Mis Reuniones en Sala Docente
          <Badge className="bg-indigo-600 text-white ml-auto">{reuniones.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {reuniones.slice(0, 6).map((r) => {
            const status = r.mi_rsvp?.status || 'pendiente';
            const meta = RSVP_META[status] || RSVP_META.pendiente;
            let fechaTxt = '';
            let horaTxt = '';
            if (r.fecha_hora_inicio) {
              try {
                const d = new Date(r.fecha_hora_inicio);
                fechaTxt = d.toLocaleDateString('es-UY', { timeZone: 'America/Montevideo', weekday: 'short', day: '2-digit', month: 'short' });
                horaTxt = d.toLocaleTimeString('es-UY', { timeZone: 'America/Montevideo', hour: '2-digit', minute: '2-digit' });
              } catch { /* noop */ }
            }
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => goToReunion(r.id)}
                className="text-left border border-gray-200 rounded-lg p-3 hover:border-indigo-400 hover:shadow-sm hover:bg-indigo-50/30 transition-all cursor-pointer group"
                data-testid={`widget-reunion-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-700 flex-1">
                    {r.titulo}
                  </h4>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.cls} font-semibold shrink-0`}>
                    {meta.icon} {meta.label}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="fas fa-calendar-day text-indigo-400"></i>
                    {fechaTxt || 'Sin fecha'}
                  </span>
                  {horaTxt && (
                    <span className="flex items-center gap-1">
                      <i className="fas fa-clock text-indigo-400"></i>
                      {horaTxt} hs
                    </span>
                  )}
                </div>
                {r.organizador_nombre && (
                  <p className="text-[11px] text-gray-500 mt-1 truncate">
                    Organiza: <span className="font-medium">{r.organizador_nombre}</span>
                  </p>
                )}
                <div className="mt-1 text-[11px] text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Toca para ver detalle →
                </div>
              </button>
            );
          })}
        </div>
        {reuniones.length > 6 && (
          <button
            type="button"
            onClick={() => navigate('/app/sala-docente?tab=reuniones')}
            className="mt-2 w-full text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
            data-testid="widget-reuniones-ver-todas"
          >
            Ver todas ({reuniones.length}) →
          </button>
        )}
      </CardContent>
    </Card>
  );
};

export default ReunionesSalaDocenteWidget;
